import React, { useState, useEffect } from 'react';
import { 
  Users, UserPlus, UserCheck, UserX, Edit, Trash2, 
  Search, Filter, CheckCircle, XCircle, Clock, Eye, EyeOff,
  Building, Award, ChevronDown, ChevronRight, Save, X,
  AlertCircle, RefreshCw
} from 'lucide-react';
import SystemAdminLayout from '../layouts/SystemAdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EvaluatorManagement = () => {
  // State
  const [evaluators, setEvaluators] = useState([]);
  const [pendingOrgs, setPendingOrgs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('evaluators'); // evaluators, assignments
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showConfirmAssignModal, setShowConfirmAssignModal] = useState(false);
  const [editingEvaluator, setEditingEvaluator] = useState(null);
  const [assigningOrg, setAssigningOrg] = useState(null);
  const [selectedEvaluatorForAssign, setSelectedEvaluatorForAssign] = useState(null);
  
  // Password visibility states
  const [showPassword, setShowPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    name: '',
    phone: '',
    bio: '',
    linkedin_url: '',
    twitter_url: '',
    credential_links: '',
    organization_affiliation: '',
    expertise_areas: [],
    status: 'active'
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const CATEGORIES = ['MICRO', 'SMALL', 'MEDIUM', 'LARGE', 'ENTERPRISE'];
  const EXPERTISE_OPTIONS = [
    'Conscious Leadership',
    'Human-Centric Culture',
    'Sustainable Products',
    'Customer Excellence',
    'Social Impact',
    'Strategic Partnerships',
    'Environmental Responsibility',
    'Organizational Evolution'
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch evaluators
      const evalResponse = await fetch(`${BACKEND_URL}/api/admin/evaluators`);
      const evalData = await evalResponse.json();
      setEvaluators(evalData.filter(e => e.status !== 'deleted'));
      
      // Fetch pending evaluation orgs
      const orgsResponse = await fetch(`${BACKEND_URL}/api/admin/pending-evaluation-orgs`);
      const orgsData = await orgsResponse.json();
      setPendingOrgs(orgsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Create Evaluator
  const handleCreateEvaluator = async () => {
    if (!formData.username || !formData.email || !formData.password || !formData.name) {
      setFormError('Please fill in all required fields');
      return;
    }
    
    setSaving(true);
    setFormError('');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/evaluators`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create evaluator');
      }
      
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      setFormError(error.message);
    } finally {
      setSaving(false);
    }
  };

  // Update Evaluator
  const handleUpdateEvaluator = async () => {
    if (!formData.name || !formData.email) {
      setFormError('Name and email are required');
      return;
    }
    
    setSaving(true);
    setFormError('');
    
    try {
      const updateData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        expertise_areas: formData.expertise_areas,
        status: formData.status
      };
      
      if (formData.password) {
        updateData.new_password = formData.password;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/admin/evaluators/${editingEvaluator.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update evaluator');
      }
      
      setShowEditModal(false);
      setEditingEvaluator(null);
      resetForm();
      fetchData();
    } catch (error) {
      setFormError(error.message);
    } finally {
      setSaving(false);
    }
  };

  // Toggle Status
  const handleToggleStatus = async (evaluator, newStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/evaluators/${evaluator.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        fetchData();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  // Delete Evaluator
  const handleDeleteEvaluator = async (evaluatorId) => {
    if (!window.confirm('Are you sure you want to delete this evaluator?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/evaluators/${evaluatorId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const data = await response.json();
        alert(data.detail || 'Failed to delete evaluator');
        return;
      }
      
      fetchData();
    } catch (error) {
      console.error('Error deleting evaluator:', error);
    }
  };

  // Assign Evaluator to Organization
  const handleAssignEvaluator = async (organizationId, evaluatorId) => {
    try {
      // First get the assessment session for this org
      const sessionsResponse = await fetch(`${BACKEND_URL}/api/assessment/detailed-status/${organizationId}`);
      const sessionData = await sessionsResponse.json();
      
      if (!sessionData.session_id) {
        alert('No active assessment found for this organization');
        return;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/admin/assign-evaluator`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assessment_id: sessionData.session_id,
          organization_id: organizationId,
          evaluator_id: evaluatorId
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to assign evaluator');
      }
      
      setShowAssignModal(false);
      setAssigningOrg(null);
      fetchData();
    } catch (error) {
      alert(error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      name: '',
      phone: '',
      bio: '',
      linkedin_url: '',
      twitter_url: '',
      credential_links: '',
      organization_affiliation: '',
      expertise_areas: [],
      status: 'active'
    });
    setFormError('');
    setShowPassword(false);
    setShowEditPassword(false);
  };

  const openEditModal = (evaluator) => {
    setEditingEvaluator(evaluator);
    setFormData({
      username: evaluator.username,
      email: evaluator.email,
      password: '',
      name: evaluator.name || evaluator.full_name,
      phone: evaluator.phone || '',
      bio: evaluator.bio || '',
      linkedin_url: evaluator.linkedin_url || '',
      twitter_url: evaluator.twitter_url || '',
      credential_links: evaluator.credential_links || '',
      organization_affiliation: evaluator.organization_affiliation || '',
      expertise_areas: evaluator.expertise_areas || [],
      status: evaluator.status || 'active'
    });
    setShowEditModal(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800 border-green-300',
      inactive: 'bg-gray-100 text-gray-800 border-gray-300',
      suspended: 'bg-red-100 text-red-800 border-red-300'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.inactive}`}>
        {status?.toUpperCase() || 'UNKNOWN'}
      </span>
    );
  };

  const filteredEvaluators = evaluators.filter(e => 
    e.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.username?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getOrgsByCategory = () => {
    if (!pendingOrgs) return [];
    if (selectedCategory === 'ALL') {
      return Object.values(pendingOrgs.by_category || {}).flat();
    }
    return pendingOrgs.by_category?.[selectedCategory] || [];
  };

  if (loading) {
    return (
      <SystemAdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </SystemAdminLayout>
    );
  }

  return (
    <SystemAdminLayout>
      <div className="bg-gray-50 min-h-full">
        {/* Page Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-8 px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-black mb-2">Evaluator Management</h1>
                <p className="text-purple-100">Manage evaluators and assign them to organizations</p>
              </div>
              <button
                onClick={() => { resetForm(); setShowCreateModal(true); }}
                className="flex items-center gap-2 px-6 py-3 bg-white text-purple-600 rounded-lg font-bold hover:bg-purple-50 transition-all shadow-lg"
                data-testid="add-evaluator-header-btn"
              >
                <UserPlus className="w-5 h-5" />
                Add Evaluator
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Evaluators</p>
                <p className="text-2xl font-bold text-gray-900">{evaluators.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Active</p>
                <p className="text-2xl font-bold text-gray-900">
                  {evaluators.filter(e => e.status === 'active').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Building className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pending Evaluations</p>
                <p className="text-2xl font-bold text-gray-900">{pendingOrgs?.total || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Award className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Assigned</p>
                <p className="text-2xl font-bold text-gray-900">
                  {getOrgsByCategory().filter(o => o.evaluator_assigned).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-4">
            <button
              onClick={() => setActiveTab('evaluators')}
              className={`px-4 py-3 font-medium border-b-2 transition-all ${
                activeTab === 'evaluators'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              Evaluators ({evaluators.length})
            </button>
            <button
              onClick={() => setActiveTab('assignments')}
              className={`px-4 py-3 font-medium border-b-2 transition-all ${
                activeTab === 'assignments'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Building className="w-4 h-4 inline mr-2" />
              Assign to Organizations ({pendingOrgs?.total || 0})
            </button>
          </nav>
        </div>

        {/* Evaluators Tab */}
        {activeTab === 'evaluators' && (
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search evaluators..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* Evaluators List */}
            <div className="bg-white rounded-xl border overflow-hidden">
              {filteredEvaluators.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">No evaluators found</p>
                  <button
                    onClick={() => { resetForm(); setShowCreateModal(true); }}
                    className="mt-4 text-purple-600 hover:underline"
                  >
                    Add your first evaluator
                  </button>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Evaluator</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Contact</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Assignments</th>
                      <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredEvaluators.map((evaluator) => (
                      <tr key={evaluator.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                              <UserCheck className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{evaluator.name || evaluator.full_name}</p>
                              <p className="text-sm text-gray-500">@{evaluator.username}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-gray-900">{evaluator.email}</p>
                          {evaluator.phone && <p className="text-sm text-gray-500">{evaluator.phone}</p>}
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(evaluator.status)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-900">{evaluator.total_assignments || 0} total</span>
                            <span className="text-xs text-gray-500">
                              ({evaluator.completed_assignments || 0} completed)
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openEditModal(evaluator)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            {evaluator.status === 'active' ? (
                              <button
                                onClick={() => handleToggleStatus(evaluator, 'inactive')}
                                className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg"
                                title="Deactivate"
                              >
                                <UserX className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleToggleStatus(evaluator, 'active')}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                                title="Activate"
                              >
                                <UserCheck className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteEvaluator(evaluator.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Assignments Tab */}
        {activeTab === 'assignments' && (
          <div className="space-y-4">
            {/* Category Filter */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-700">Filter by Category:</span>
              <button
                onClick={() => setSelectedCategory('ALL')}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === 'ALL'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All ({pendingOrgs?.total || 0})
              </button>
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === cat
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cat} ({pendingOrgs?.summary?.[cat] || 0})
                </button>
              ))}
            </div>

            {/* Organizations List */}
            <div className="bg-white rounded-xl border overflow-hidden">
              {getOrgsByCategory().length === 0 ? (
                <div className="text-center py-12">
                  <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">No organizations pending evaluation in this category</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Organization</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Category</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Assigned Evaluator</th>
                      <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {getOrgsByCategory().map((org) => (
                      <tr key={org.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <Building className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{org.name}</p>
                              <p className="text-sm text-gray-500">{org.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                            {org.organization_size}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                            Pending Evaluation
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {org.evaluator_assigned ? (
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              <span className="text-sm text-gray-900">{org.evaluator_name}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">Not assigned</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => { setAssigningOrg(org); setShowAssignModal(true); }}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                              org.evaluator_assigned
                                ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                : 'bg-purple-600 text-white hover:bg-purple-700'
                            }`}
                          >
                            {org.evaluator_assigned ? 'Reassign' : 'Assign Evaluator'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
          </div>
        </div>

        {/* Create Evaluator Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Add New Evaluator</h2>
                <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                {formError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {formError}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="John Doe"
                    autoComplete="off"
                    data-testid="evaluator-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="johndoe"
                    autoComplete="off"
                    data-testid="evaluator-username-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="john@example.com"
                    autoComplete="off"
                    data-testid="evaluator-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="••••••••"
                      autoComplete="new-password"
                      data-testid="evaluator-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-purple-600 transition-colors"
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="+1 234 567 8900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Short Bio</label>
                  <textarea
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Brief description of expertise and background..."
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organization/Company Affiliation</label>
                  <input
                    type="text"
                    value={formData.organization_affiliation}
                    onChange={(e) => setFormData({ ...formData, organization_affiliation: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g., XYZ Consulting, ABC University"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn URL</label>
                    <input
                      type="url"
                      value={formData.linkedin_url}
                      onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="https://linkedin.com/in/..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Twitter/X URL</label>
                    <input
                      type="url"
                      value={formData.twitter_url}
                      onChange={(e) => setFormData({ ...formData, twitter_url: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="https://twitter.com/..."
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Credential Links</label>
                  <input
                    type="text"
                    value={formData.credential_links}
                    onChange={(e) => setFormData({ ...formData, credential_links: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Certifications, portfolio links (comma-separated)"
                  />
                  <p className="text-xs text-gray-500 mt-1">Add links to certifications, portfolio, or credentials</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="p-6 border-t flex justify-end gap-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateEvaluator}
                  disabled={saving}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Create Evaluator
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Evaluator Modal */}
        {showEditModal && editingEvaluator && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Edit Evaluator</h2>
                <button onClick={() => { setShowEditModal(false); setEditingEvaluator(null); }} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                {formError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {formError}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Username (Login ID)</label>
                  <input
                    type="text"
                    value={formData.username}
                    disabled
                    className="w-full px-4 py-2 border rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                    data-testid="edit-evaluator-username-readonly"
                  />
                  <p className="text-xs text-gray-500 mt-1">Username cannot be changed after creation</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password (leave blank to keep current)</label>
                  <div className="relative">
                    <input
                      type={showEditPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="••••••••"
                      autoComplete="new-password"
                      data-testid="edit-evaluator-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowEditPassword(!showEditPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-purple-600 transition-colors"
                      data-testid="toggle-edit-password-visibility"
                    >
                      {showEditPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
              </div>
              <div className="p-6 border-t flex justify-end gap-3">
                <button
                  onClick={() => { setShowEditModal(false); setEditingEvaluator(null); }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateEvaluator}
                  disabled={saving}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Assign Evaluator Modal */}
        {showAssignModal && assigningOrg && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl w-full max-w-lg mx-4">
              <div className="p-6 border-b flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Assign Evaluator</h2>
                <button onClick={() => { setShowAssignModal(false); setAssigningOrg(null); }} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6">
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Assigning evaluator to:</p>
                  <p className="font-bold text-gray-900">{assigningOrg.name}</p>
                  <p className="text-sm text-gray-500">{assigningOrg.organization_size} Organization</p>
                </div>
                
                <p className="text-sm font-medium text-gray-700 mb-3">Select an Evaluator:</p>
                
                {evaluators.filter(e => e.status === 'active').length === 0 ? (
                  <div className="text-center py-8">
                    <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-600">No active evaluators available</p>
                    <button
                      onClick={() => { setShowAssignModal(false); setShowCreateModal(true); }}
                      className="mt-2 text-purple-600 hover:underline"
                    >
                      Create a new evaluator
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {evaluators.filter(e => e.status === 'active').map((evaluator) => (
                      <button
                        key={evaluator.id}
                        onClick={() => {
                          setSelectedEvaluatorForAssign(evaluator);
                          setShowConfirmAssignModal(true);
                        }}
                        className="w-full p-3 border rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-all text-left flex items-center gap-3"
                        data-testid={`select-evaluator-${evaluator.id}`}
                      >
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          <UserCheck className="w-5 h-5 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{evaluator.name || evaluator.full_name}</p>
                          <p className="text-sm text-gray-500">{evaluator.email}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">{evaluator.total_assignments || 0} assignments</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-6 border-t">
                <button
                  onClick={() => { setShowAssignModal(false); setAssigningOrg(null); }}
                  className="w-full px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirm Assignment Modal */}
        {showConfirmAssignModal && selectedEvaluatorForAssign && assigningOrg && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-xl w-full max-w-md mx-4">
              <div className="p-6 border-b">
                <h2 className="text-xl font-bold text-gray-900">Confirm Assignment</h2>
              </div>
              <div className="p-6">
                <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="text-sm text-purple-700 mb-2">You are about to assign:</p>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <UserCheck className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">{selectedEvaluatorForAssign.name || selectedEvaluatorForAssign.full_name}</p>
                      <p className="text-sm text-gray-500">{selectedEvaluatorForAssign.email}</p>
                    </div>
                  </div>
                  <p className="text-sm text-purple-700">To organization:</p>
                  <div className="flex items-center gap-3 mt-2">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Building className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">{assigningOrg.name}</p>
                      <p className="text-sm text-gray-500">{assigningOrg.organization_size} Organization</p>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  This evaluator will be responsible for reviewing and scoring this organization&apos;s assessment.
                </p>
              </div>
              <div className="p-6 border-t flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowConfirmAssignModal(false);
                    setSelectedEvaluatorForAssign(null);
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                  data-testid="cancel-assignment-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    handleAssignEvaluator(assigningOrg.id, selectedEvaluatorForAssign.id);
                    setShowConfirmAssignModal(false);
                    setSelectedEvaluatorForAssign(null);
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                  data-testid="confirm-assignment-btn"
                >
                  <CheckCircle className="w-4 h-4" />
                  Confirm Assignment
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SystemAdminLayout>
  );
};

export default EvaluatorManagement;
